import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

// Mobile breakpoint matching Tailwind's lg: (1024px)
const MOBILE_BREAKPOINT = 1024

interface SidebarContextType {
  isCollapsed: boolean
  setIsCollapsed: (value: boolean) => void
  toggle: () => void
  // Mobile-specific state
  isMobile: boolean
  openMobile: boolean
  setOpenMobile: (value: boolean) => void
  toggleMobile: () => void
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined)

export function SidebarProvider({ children }: { children: ReactNode }) {
  // Desktop collapsed state (persisted)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('sidebar-collapsed') === 'true'
    }
    return false
  })

  // Mobile state
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < MOBILE_BREAKPOINT
    }
    return false
  })
  const [openMobile, setOpenMobile] = useState(false)

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(isCollapsed))
  }, [isCollapsed])

  // Listen for window resize to detect mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT
      setIsMobile(mobile)
      // Close mobile drawer when resizing to desktop
      if (!mobile) {
        setOpenMobile(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const toggle = () => setIsCollapsed(!isCollapsed)
  const toggleMobile = () => setOpenMobile(!openMobile)

  return (
    <SidebarContext.Provider value={{
      isCollapsed,
      setIsCollapsed,
      toggle,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleMobile,
    }}>
      {children}
    </SidebarContext.Provider>
  )
}

export function useSidebar() {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}
